/**
 * Mağazanın kendi sayfasında çizilen arama sonuç sayfası.
 *
 * - **Durum adres çubuğunda:** sorgu `?q=` (arama çubuğunun `searchUrl`'i
 *   buraya yollar), diğer her şey önekli (`?shs_brand=…&shs_page=2`) — mağaza
 *   platformunun kendi parametreleriyle çakışmasın. Link paylaşılır, geri tuşu
 *   çalışır.
 * - **Her şey servis yanıtından:** filtreler, sıralamalar, fiyat dilimleri,
 *   kart alt satırı, sayfa boyutu panelden yönetiliyor; burada kural yok.
 * - **Maliyet:** aynı parametreler istemci cache'inden gelir; filtre içi arama
 *   yalnız yazınca, debounce'lu.
 */

import {
  AiSearchQueryError,
  type AiSearchQueryResponse,
  type IAiSearchQueryResult,
  type ISearchClient,
} from "./client";
import type { IAiSearchFacet, IAiSearchFacetValue } from "./search-core";
import { injectResultsStyles } from "./results-styles";

export interface IResultsTexts {
  title: string;
  titleAll: string;
  count: string;
  sort: string;
  filters: string;
  clearAll: string;
  close: string;
  showMore: string;
  showLess: string;
  searchIn: string;
  priceMin: string;
  priceMax: string;
  apply: string;
  didYouMean: string;
  empty: string;
  emptyAll: string;
  emptyHint: string;
  loading: string;
  outOfStock: string;
  prev: string;
  next: string;
  errorGeneric: string;
  errorRateLimit: string;
  errorBilling: string;
  errorAuth: string;
}

const DEFAULT_TEXTS: IResultsTexts = {
  title: "“{q}” için sonuçlar",
  titleAll: "Tüm ürünler",
  count: "{count} ürün",
  sort: "Sırala",
  filters: "Filtreler",
  clearAll: "Tümünü temizle",
  close: "Kapat",
  showMore: "Daha fazla göster",
  showLess: "Daha az göster",
  searchIn: "{label} içinde ara",
  priceMin: "En az",
  priceMax: "En çok",
  apply: "Uygula",
  didYouMean: "Bunu mu demek istediniz:",
  empty: "“{q}” için ürün bulunamadı.",
  emptyAll: "Ürün bulunamadı.",
  emptyHint: "Farklı bir kelime deneyin ya da filtreleri azaltın.",
  loading: "Yükleniyor…",
  outOfStock: "Tükendi",
  prev: "Önceki",
  next: "Sonraki",
  errorGeneric: "Arama şu an kullanılamıyor.",
  errorRateLimit: "Çok fazla arama yapıldı, birazdan tekrar deneyin.",
  errorBilling: "Arama servisi geçici olarak kullanılamıyor.",
  errorAuth: "Arama servisi bu mağaza için etkin değil.",
};

export type TResultsTheme = "light" | "dark" | "auto";

export interface IResultsUiOptions {
  client: ISearchClient;
  /** Sayfanın çizileceği kapsayıcı (seçici ya da eleman). Zorunlu. */
  mount: string | Element;
  currency?: string;
  locale?: string;
  theme?: TResultsTheme;
  /** `false` → CSS enjekte edilmez, mağaza kendi stilini yazar. */
  styles?: boolean;
  /** Ürün linki yoksa şablon: `{code}`, `{id}`. Servis `url` döndürüyorsa o kullanılır. */
  productUrl?: string;
  /** Sorgu parametresi; varsayılan `q` (arama çubuğunun `searchUrl`'iyle aynı olmalı). */
  queryParam?: string;
  /** Diğer parametrelerin öneki; varsayılan `shs_`. */
  paramPrefix?: string;
  /** Geniş ekranda kolon sayısı (2–6); dar ekranlar kendini daraltır. */
  columns?: number;
  texts?: Partial<IResultsTexts>;
}

export interface IResultsUi {
  /** Adres çubuğunu yeniden okuyup arar (SPA'larda rota değişince). */
  refresh: () => void;
  setTheme: (theme: TResultsTheme) => void;
  destroy: () => void;
}

/** Servisin kabul ettiği parametreler; adres çubuğundan yalnız bunlar okunur. */
const SERVICE_PARAMS = new Set([
  "page",
  "sort",
  "per_page",
  "price",
  "price_min",
  "price_max",
  "rating",
  "in_stock",
  "discount",
  "category",
  "brand",
  "model",
]);
const isServiceParam = (key: string) =>
  SERVICE_PARAMS.has(key) || /^opt_[a-z0-9_]{1,40}$/.test(key);

/** Servis üst sınırı (`MAX_PAGE`). */
const MAX_PAGE = 250;
const COLLAPSED_VALUES = 8;
const FACET_SEARCH_DEBOUNCE_MS = 300;

const escapeHtml = (value: string) =>
  value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

const fill = (template: string, values: Record<string, string>) =>
  template.replace(/\{(\w+)\}/g, (match, key) =>
    key in values ? values[key] : match,
  );

const resolveElement = (target: string | Element): Element | null =>
  typeof target === "string" ? document.querySelector(target) : target;

export const createResultsUi = (options: IResultsUiOptions): IResultsUi => {
  const {
    client,
    currency,
    locale,
    productUrl,
    styles: withStyles = true,
  } = options;
  const queryParam = options.queryParam || "q";
  const prefix = options.paramPrefix ?? "shs_";
  const texts: IResultsTexts = { ...DEFAULT_TEXTS, ...(options.texts || {}) };

  const host = resolveElement(options.mount);
  if (!host) {
    throw new Error(
      `[sendheap-search] sonuç sayfası kapsayıcısı bulunamadı: ${String(options.mount)}`,
    );
  }
  if (withStyles) injectResultsStyles();

  const money = (() => {
    try {
      return new Intl.NumberFormat(locale || undefined, {
        style: currency ? "currency" : "decimal",
        currency: currency || undefined,
        maximumFractionDigits: 2,
      });
    } catch {
      return null;
    }
  })();
  const formatPrice = (value: number) =>
    money ? money.format(value) : String(value);
  const formatCount = (value: number) =>
    new Intl.NumberFormat(locale || undefined).format(value);

  /* --- DOM iskeleti ------------------------------------------------- */

  const root = document.createElement("div");
  root.className = "shs-results";
  root.setAttribute("data-theme", options.theme || "light");
  const columns = Math.trunc(options.columns ?? 0);
  if (columns >= 2 && columns <= 6)
    root.style.setProperty("--shs-columns", String(columns));

  root.innerHTML =
    '<div class="shs-results__head">' +
    '<h2 class="shs-results__title"></h2>' +
    '<div class="shs-results__tools">' +
    `<button type="button" class="shs-results__filters-btn" data-act="open-filters">${escapeHtml(texts.filters)}</button>` +
    `<select class="shs-results__sort" aria-label="${escapeHtml(texts.sort)}" hidden></select>` +
    "</div></div>" +
    '<p class="shs-results__suggestion" hidden></p>' +
    '<div class="shs-results__chips"></div>' +
    '<div class="shs-results__layout">' +
    '<div class="shs-results__overlay" data-act="close-filters" hidden></div>' +
    `<aside class="shs-results__facets" aria-label="${escapeHtml(texts.filters)}">` +
    `<div class="shs-results__drawer-head"><span>${escapeHtml(texts.filters)}</span>` +
    `<button type="button" class="shs-results__btn" data-act="close-filters">${escapeHtml(texts.close)}</button></div>` +
    '<div class="shs-results__facet-list"></div></aside>' +
    '<div class="shs-results__main">' +
    '<ul class="shs-results__grid"></ul>' +
    '<div class="shs-results__state" hidden></div>' +
    '<nav class="shs-pager"></nav>' +
    "</div></div>";
  host.appendChild(root);

  const $ = <T extends Element>(selector: string) =>
    root.querySelector(selector) as T;
  const title = $<HTMLElement>(".shs-results__title");
  const sortSelect = $<HTMLSelectElement>(".shs-results__sort");
  const filtersBtn = $<HTMLButtonElement>(".shs-results__filters-btn");
  const suggestionEl = $<HTMLElement>(".shs-results__suggestion");
  const chips = $<HTMLElement>(".shs-results__chips");
  const layout = $<HTMLElement>(".shs-results__layout");
  const overlay = $<HTMLElement>(".shs-results__overlay");
  const aside = $<HTMLElement>(".shs-results__facets");
  const facetList = $<HTMLElement>(".shs-results__facet-list");
  const main = $<HTMLElement>(".shs-results__main");
  const grid = $<HTMLUListElement>(".shs-results__grid");
  const stateEl = $<HTMLElement>(".shs-results__state");
  const pager = $<HTMLElement>(".shs-pager");

  /* --- adres çubuğu ↔ servis parametreleri --------------------------- */

  const readParams = () => {
    const url = new URLSearchParams(window.location.search);
    const params = new URLSearchParams();
    params.set("q", (url.get(queryParam) || "").slice(0, 200));
    url.forEach((value, key) => {
      if (!prefix || !key.startsWith(prefix)) return;
      const name = key.slice(prefix.length);
      if (isServiceParam(name) && value !== "") params.append(name, value);
    });
    return params;
  };

  const writeParams = (params: URLSearchParams) => {
    const url = new URLSearchParams(window.location.search);
    [...url.keys()]
      .filter((key) => prefix && key.startsWith(prefix))
      .forEach((key) => url.delete(key));
    const q = params.get("q") || "";
    if (q) url.set(queryParam, q);
    else url.delete(queryParam);
    params.forEach((value, key) => {
      if (key !== "q") url.append(`${prefix}${key}`, value);
    });
    const search = url.toString();
    window.history.pushState(
      null,
      "",
      `${window.location.pathname}${search ? `?${search}` : ""}${window.location.hash}`,
    );
  };

  /* --- durum -------------------------------------------------------- */

  let current: AiSearchQueryResponse | null = null;
  let params = readParams();
  let controller: AbortController | null = null;
  let destroyed = false;
  const expanded = new Set<string>();
  /** Filtre içi arama: anahtar → yazılan + gelen değerler. */
  const facetSearch = new Map<string, { needle: string; values: IAiSearchFacetValue[] }>();
  let facetSearchTimer: ReturnType<typeof setTimeout> | null = null;

  const errorTextFor = (error: unknown) => {
    if (error instanceof AiSearchQueryError) {
      if (error.isRateLimited) return texts.errorRateLimit;
      if (error.isAuthError || error.isOriginError || error.isLicenceError)
        return texts.errorAuth;
      if (error.isBillingError) return texts.errorBilling;
    }
    return texts.errorGeneric;
  };

  const showState = (html: string | null, variant?: "error") => {
    stateEl.hidden = !html;
    stateEl.className =
      variant === "error"
        ? "shs-results__state shs-results__state--error"
        : "shs-results__state";
    stateEl.innerHTML = html || "";
  };

  /* --- çizim -------------------------------------------------------- */

  const optionRow = (
    act: string,
    key: string,
    value: string,
    label: string,
    selected: boolean,
    count: number | null | undefined,
  ) =>
    `<li><label class="shs-facet__option"${count === 0 && !selected ? " data-empty" : ""}>` +
    `<input type="checkbox" class="shs-facet__check" data-act="${act}" data-key="${escapeHtml(key)}" data-value="${escapeHtml(value)}"${selected ? " checked" : ""}>` +
    `<span class="shs-facet__label">${label}</span>` +
    (count === null || count === undefined
      ? ""
      : `<span class="shs-facet__count">${escapeHtml(formatCount(count))}</span>`) +
    "</label></li>";

  const renderValueFacet = (facet: IAiSearchFacet) => {
    const searched = facetSearch.get(facet.key);
    const values = searched ? searched.values : (facet.values ?? []);
    if (!searched && values.length === 0) return "";
    const isOpen = expanded.has(facet.key) || Boolean(searched);
    const visible = isOpen ? values : values.slice(0, COLLAPSED_VALUES);
    const searchBox =
      facet.searchable && ((facet.values?.length ?? 0) > COLLAPSED_VALUES || searched)
        ? `<input type="search" class="shs-facet__search" data-act="facet-search" data-key="${escapeHtml(facet.key)}" ` +
          `placeholder="${escapeHtml(fill(texts.searchIn, { label: facet.label }))}" value="${escapeHtml(searched?.needle ?? "")}">`
        : "";
    const more =
      !searched && values.length > COLLAPSED_VALUES
        ? `<button type="button" class="shs-facet__more" data-act="more" data-key="${escapeHtml(facet.key)}">` +
          `${escapeHtml(isOpen ? texts.showLess : texts.showMore)}</button>`
        : "";
    return (
      `<div class="shs-facet" data-facet="${escapeHtml(facet.key)}">` +
      `<h3 class="shs-facet__title">${escapeHtml(facet.label)}</h3>${searchBox}` +
      `<ul class="shs-facet__list">${visible
        .map((item) =>
          optionRow(
            "value",
            facet.key,
            String(item.value),
            escapeHtml(item.label ?? String(item.value)),
            item.selected,
            item.count,
          ),
        )
        .join("")}</ul>${more}</div>`
    );
  };

  const renderFacet = (facet: IAiSearchFacet) => {
    if (facet.type === "value") return renderValueFacet(facet);
    if (facet.type === "toggle") {
      return (
        `<div class="shs-facet" data-facet="${escapeHtml(facet.key)}"><ul class="shs-facet__list">` +
        optionRow("toggle", facet.key, "1", escapeHtml(facet.label), Boolean(facet.selected), facet.count) +
        "</ul></div>"
      );
    }
    const values = facet.values ?? [];
    const rows = values
      .map((item) =>
        optionRow(
          facet.type === "rating" ? "rating" : "value",
          facet.key,
          String(item.value),
          facet.type === "rating"
            ? `<span class="shs-facet__stars" aria-hidden="true">${"★".repeat(Number(item.value))}${"☆".repeat(Math.max(0, 5 - Number(item.value)))}</span> ${escapeHtml(item.label ?? "")}`
            : escapeHtml(item.label ?? String(item.value)),
          item.selected,
          item.count,
        ),
      )
      .join("");
    const range =
      facet.type === "price"
        ? '<div class="shs-facet__range">' +
          `<input type="number" inputmode="decimal" min="0" data-range="min" placeholder="${escapeHtml(texts.priceMin)}" value="${escapeHtml(params.get("price_min") ?? "")}">` +
          `<input type="number" inputmode="decimal" min="0" data-range="max" placeholder="${escapeHtml(texts.priceMax)}" value="${escapeHtml(params.get("price_max") ?? "")}">` +
          `<button type="button" class="shs-results__btn" data-act="price-range">${escapeHtml(texts.apply)}</button></div>`
        : "";
    return (
      `<div class="shs-facet" data-facet="${escapeHtml(facet.key)}">` +
      `<h3 class="shs-facet__title">${escapeHtml(facet.label)}</h3>` +
      `<ul class="shs-facet__list">${rows}</ul>${range}</div>`
    );
  };

  const renderChips = (data: AiSearchQueryResponse) => {
    const items: string[] = [];
    const chip = (act: string, key: string, value: string, label: string) =>
      `<button type="button" class="shs-chip" data-act="${act}" data-key="${escapeHtml(key)}" data-value="${escapeHtml(value)}">` +
      `${escapeHtml(label)} <span aria-hidden="true">×</span></button>`;
    data.facets.forEach((facet) => {
      if (facet.type === "toggle") {
        if (facet.selected) items.push(chip("toggle", facet.key, "1", facet.label));
        return;
      }
      (facet.values ?? [])
        .filter((item) => item.selected)
        .forEach((item) =>
          items.push(
            chip(
              facet.type === "rating" ? "rating" : "value",
              facet.key,
              String(item.value),
              facet.type === "value"
                ? `${facet.label}: ${item.label ?? item.value}`
                : String(item.label ?? item.value),
            ),
          ),
        );
    });
    const min = params.get("price_min");
    const max = params.get("price_max");
    if (min) items.push(chip("remove", "price_min", min, `${texts.priceMin}: ${formatPrice(Number(min))}`));
    if (max) items.push(chip("remove", "price_max", max, `${texts.priceMax}: ${formatPrice(Number(max))}`));
    if (items.length > 1) {
      items.push(
        `<button type="button" class="shs-chip shs-chip--clear" data-act="clear">${escapeHtml(texts.clearAll)}</button>`,
      );
    }
    chips.innerHTML = items.join("");
    const activeCount = items.length > 1 ? items.length - 1 : items.length;
    filtersBtn.textContent = activeCount
      ? `${texts.filters} (${activeCount})`
      : texts.filters;
  };

  const productHref = (item: IAiSearchQueryResult) =>
    item.url ||
    (productUrl
      ? fill(productUrl, {
          code: encodeURIComponent(item.code || ""),
          id: encodeURIComponent(item.id || ""),
        })
      : "");

  const renderCard = (item: IAiSearchQueryResult) => {
    const href = productHref(item);
    const tag = href ? "a" : "div";
    const subtitle = item.subtitle || item.brand || "";
    const discount = Math.round(item.discount || 0);
    return (
      `<li><${tag} class="shs-card"${href ? ` href="${escapeHtml(href)}"` : ""}>` +
      '<span class="shs-card__media">' +
      (item.image
        ? `<img class="shs-card__image" src="${escapeHtml(item.image)}" alt="${escapeHtml(item.title)}" loading="lazy">`
        : '<span class="shs-card__placeholder"></span>') +
      (discount > 0 ? `<span class="shs-card__badge">%${discount}</span>` : "") +
      (!item.in_stock
        ? `<span class="shs-card__badge shs-card__badge--out">${escapeHtml(texts.outOfStock)}</span>`
        : "") +
      "</span>" +
      '<span class="shs-card__body">' +
      (subtitle ? `<span class="shs-card__subtitle">${escapeHtml(subtitle)}</span>` : "") +
      `<span class="shs-card__title">${escapeHtml(item.title)}</span>` +
      (item.rating > 0
        ? `<span class="shs-card__rating"><span aria-hidden="true">★</span> ${escapeHtml(item.rating.toLocaleString(locale || undefined, { maximumFractionDigits: 1 }))}` +
          (item.reviews ? ` (${escapeHtml(formatCount(item.reviews))})` : "") +
          "</span>"
        : "") +
      '<span class="shs-card__prices">' +
      `<span class="shs-card__price">${escapeHtml(formatPrice(item.price))}</span>` +
      (item.list_price > item.price
        ? `<s class="shs-card__list-price">${escapeHtml(formatPrice(item.list_price))}</s>`
        : "") +
      `</span></span></${tag}></li>`
    );
  };

  const renderPager = (data: AiSearchQueryResponse) => {
    const perPage = data.per_page || data.results.length || 1;
    const total = Math.min(MAX_PAGE, Math.ceil(data.found / perPage));
    if (total <= 1) {
      pager.innerHTML = "";
      return;
    }
    const page = data.page || 1;
    const pages = new Set([1, total, page - 1, page, page + 1]);
    const list = [...pages].filter((n) => n >= 1 && n <= total).sort((a, b) => a - b);
    const btn = (n: number, label: string, disabled = false, currentPage = false) =>
      `<button type="button" class="shs-pager__btn" data-act="page" data-value="${n}"` +
      `${disabled ? " disabled" : ""}${currentPage ? ' aria-current="page"' : ""}>${escapeHtml(label)}</button>`;
    let html = btn(page - 1, texts.prev, page <= 1);
    list.forEach((n, index) => {
      if (index > 0 && n - list[index - 1] > 1)
        html += '<span class="shs-pager__gap">…</span>';
      html += btn(n, String(n), false, n === page);
    });
    html += btn(page + 1, texts.next, page >= total);
    pager.innerHTML = html;
  };

  const render = (data: AiSearchQueryResponse) => {
    current = data;
    const q = data.query || "";
    title.innerHTML =
      escapeHtml(q ? fill(texts.title, { q }) : texts.titleAll) +
      ` <span class="shs-results__count">${escapeHtml(fill(texts.count, { count: formatCount(data.found) }))}</span>`;

    // Sıralama: servisin açık seçenekleri, seçili olan yanıttaki `sort`.
    sortSelect.hidden = data.sorts.length === 0;
    sortSelect.innerHTML = data.sorts
      .map(
        (sort) =>
          `<option value="${escapeHtml(sort.key)}"${sort.key === data.sort ? " selected" : ""}>${escapeHtml(sort.label)}</option>`,
      )
      .join("");

    suggestionEl.hidden = !data.suggestion;
    suggestionEl.innerHTML = data.suggestion
      ? `${escapeHtml(texts.didYouMean)} <a data-act="suggestion" data-value="${escapeHtml(data.suggestion)}">${escapeHtml(data.suggestion)}</a>`
      : "";

    const facetsHtml = data.facets.map(renderFacet).join("");
    facetList.innerHTML = facetsHtml;
    const noFacets = !facetsHtml;
    aside.hidden = noFacets;
    filtersBtn.hidden = noFacets;
    if (noFacets) layout.setAttribute("data-no-facets", "");
    else layout.removeAttribute("data-no-facets");
    renderChips(data);

    grid.innerHTML = data.results.map(renderCard).join("");
    if (data.results.length === 0) {
      showState(
        `<strong>${escapeHtml(q ? fill(texts.empty, { q }) : texts.emptyAll)}</strong>${escapeHtml(texts.emptyHint)}`,
      );
    } else {
      showState(null);
    }
    renderPager(data);
  };

  const load = () => {
    controller?.abort();
    controller = new AbortController();
    const signal = controller.signal;
    if (current) main.setAttribute("data-loading", "");
    else showState(escapeHtml(texts.loading));

    client
      .listing(params, { signal })
      .then((data) => {
        if (destroyed || signal.aborted) return;
        main.removeAttribute("data-loading");
        render(data);
      })
      .catch((error: unknown) => {
        if (destroyed || signal.aborted) return;
        if (error instanceof DOMException && error.name === "AbortError") return;
        main.removeAttribute("data-loading");
        grid.innerHTML = "";
        pager.innerHTML = "";
        showState(escapeHtml(errorTextFor(error)), "error");
      });
  };

  /** Parametre değişikliği: adres çubuğu + arama; sayfa başına kaydır. */
  const apply = (next: URLSearchParams, { resetPage = true, scroll = false } = {}) => {
    if (resetPage) next.delete("page");
    params = next;
    facetSearch.clear();
    writeParams(params);
    load();
    if (scroll) {
      const top = root.getBoundingClientRect().top;
      if (top < 0) root.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const toggleMulti = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    const values = next.getAll(key);
    next.delete(key);
    (values.includes(value) ? values.filter((v) => v !== value) : [...values, value]).forEach(
      (v) => next.append(key, v),
    );
    apply(next);
  };

  const setSingle = (key: string, value: string | null) => {
    const next = new URLSearchParams(params);
    if (value === null || next.get(key) === value) next.delete(key);
    else next.set(key, value);
    apply(next);
  };

  const setDrawer = (open: boolean) => {
    if (open) aside.setAttribute("data-open", "");
    else aside.removeAttribute("data-open");
    overlay.hidden = !open;
  };

  /* --- olaylar (tek dinleyici) --------------------------------------- */

  const onClick = (event: Event) => {
    const target = (event.target as Element).closest<HTMLElement>("[data-act]");
    if (!target || !root.contains(target)) return;
    const { act, key = "", value = "" } = target.dataset;
    switch (act) {
      case "value":
        toggleMulti(key, value);
        break;
      case "rating":
        setSingle(key, value);
        break;
      case "toggle":
        setSingle(key, "1");
        break;
      case "remove":
        setSingle(key, null);
        break;
      case "more":
        if (expanded.has(key)) expanded.delete(key);
        else expanded.add(key);
        if (current) render(current);
        break;
      case "clear": {
        const next = new URLSearchParams({ q: params.get("q") || "" });
        const sort = params.get("sort");
        if (sort) next.set("sort", sort);
        apply(next);
        break;
      }
      case "page": {
        const next = new URLSearchParams(params);
        next.set("page", value);
        apply(next, { resetPage: false, scroll: true });
        break;
      }
      case "suggestion":
        event.preventDefault();
        apply(new URLSearchParams({ q: value }));
        break;
      case "price-range": {
        const read = (which: string) =>
          (target.parentElement?.querySelector<HTMLInputElement>(`[data-range="${which}"]`)?.value ?? "").trim();
        const next = new URLSearchParams(params);
        const min = read("min");
        const max = read("max");
        if (min) next.set("price_min", min);
        else next.delete("price_min");
        if (max) next.set("price_max", max);
        else next.delete("price_max");
        apply(next);
        break;
      }
      case "open-filters":
        setDrawer(true);
        break;
      case "close-filters":
        setDrawer(false);
        break;
      default:
        break;
    }
  };

  const onChange = (event: Event) => {
    if (event.target === sortSelect) {
      const next = new URLSearchParams(params);
      next.set("sort", sortSelect.value);
      apply(next);
    }
  };

  const onInput = (event: Event) => {
    const input = event.target as HTMLInputElement;
    if (input.dataset.act !== "facet-search") return;
    const key = input.dataset.key || "";
    const needle = input.value;
    if (facetSearchTimer !== null) clearTimeout(facetSearchTimer);
    facetSearchTimer = setTimeout(() => {
      if (!needle.trim()) {
        facetSearch.delete(key);
        if (current) render(current);
        focusSearch(key);
        return;
      }
      client
        .facetValues(key, needle, params)
        .then((data) => {
          if (destroyed) return;
          facetSearch.set(key, { needle, values: data.values });
          if (current) render(current);
          focusSearch(key);
        })
        .catch(() => {
          // Filtre içi arama süs: hata durumunda liste olduğu gibi kalır.
        });
    }, FACET_SEARCH_DEBOUNCE_MS);
  };

  /** Liste yeniden çizilince yazılan kutu odakta kalsın. */
  const focusSearch = (key: string) => {
    const input = root.querySelector<HTMLInputElement>(
      `.shs-facet__search[data-key="${CSS.escape(key)}"]`,
    );
    if (!input) return;
    input.focus();
    const end = input.value.length;
    input.setSelectionRange(end, end);
  };

  const onKeydown = (event: KeyboardEvent) => {
    if (event.key === "Escape" && aside.hasAttribute("data-open")) setDrawer(false);
    const input = event.target as HTMLInputElement;
    if (event.key === "Enter" && input.dataset?.range) {
      input.parentElement?.querySelector<HTMLElement>('[data-act="price-range"]')?.click();
    }
  };

  const onPopState = () => {
    params = readParams();
    facetSearch.clear();
    load();
  };

  root.addEventListener("click", onClick);
  root.addEventListener("change", onChange);
  root.addEventListener("input", onInput);
  root.addEventListener("keydown", onKeydown);
  window.addEventListener("popstate", onPopState);

  load();

  return {
    refresh: onPopState,
    setTheme: (theme) => root.setAttribute("data-theme", theme),
    destroy: () => {
      destroyed = true;
      controller?.abort();
      if (facetSearchTimer !== null) clearTimeout(facetSearchTimer);
      root.removeEventListener("click", onClick);
      root.removeEventListener("change", onChange);
      root.removeEventListener("input", onInput);
      root.removeEventListener("keydown", onKeydown);
      window.removeEventListener("popstate", onPopState);
      root.remove();
    },
  };
};
